/**
 * Perspective ("side view" / mockup) templates.
 *
 * Some backgrounds are photos of a card lying at an angle. The design stays on the flat letter page;
 * template.perspective = { tl: [x, y], tr, br, bl } (% of the page) says where the flat page's corners
 * land on the background, and the whole design layer is warped onto that quad with a CSS matrix3d.
 * The server does the same warp with Pillow (backend/app/core/perspective.py): keep both in sync.
 */

export const CORNERS = ['tl', 'tr', 'br', 'bl'];
const FLAT = [[0, 0], [100, 0], [100, 100], [0, 100]];
const MIN = -50;
const MAX = 150;

const toPoints = (p) => CORNERS.map((k) => p[k]);

// Corners already checked and clamped (normalizePerspective), or derived from such corners for display
// (coverPerspective, perspectiveForBackground). Derived corners may legitimately sit outside MIN..MAX
// (a 5:7 card crop stretches them), so they are never clamped again.
const CHECKED = Symbol('perspectiveChecked');
const checked = (quad) => Object.defineProperty(quad, CHECKED, { value: true });

export const isConvex = (pts) => {
  let sign = 0;
  for (let i = 0; i < 4; i += 1) {
    const [x0, y0] = pts[i];
    const [x1, y1] = pts[(i + 1) % 4];
    const [x2, y2] = pts[(i + 2) % 4];
    const cross = (x1 - x0) * (y2 - y1) - (y1 - y0) * (x2 - x1);
    if (Math.abs(cross) < 1e-6) return false;
    const s = cross > 0 ? 1 : -1;
    if (sign && s !== sign) return false;
    sign = s;
  }
  let area = 0;
  for (let i = 0; i < 4; i += 1) {
    const [x0, y0] = pts[i];
    const [x1, y1] = pts[(i + 1) % 4];
    area += x0 * y1 - x1 * y0;
  }
  return Math.abs(area) / 2 >= 4;
};

/** A clean { tl, tr, br, bl } or null when the value isn't a usable, convex quad (mirrors normalize()). */
export const normalizePerspective = (value) => {
  if (!value || typeof value !== 'object' || value.enabled === false) return null;
  if (value[CHECKED]) return value;
  const pts = [];
  for (const k of CORNERS) {
    let p = value[k];
    if (p && !Array.isArray(p) && typeof p === 'object') p = [p.x, p.y];
    if (!Array.isArray(p) || p.length !== 2) return null;
    const x = Number(p[0]);
    const y = Number(p[1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    pts.push([Math.max(MIN, Math.min(MAX, x)), Math.max(MIN, Math.min(MAX, y))]);
  }
  if (!isConvex(pts)) return null;
  if (pts.every(([x, y], i) => Math.abs(x - FLAT[i][0]) < 0.01 && Math.abs(y - FLAT[i][1]) < 0.01)) return null;
  return checked(Object.fromEntries(CORNERS.map((k, i) => [k, pts[i]])));
};

/** Homography [a,b,c,d,e,f,g,h] taking the unit square to the quad: X = (a u + b v + c) / (g u + h v + 1). */
export const squareToQuad = ([[x0, y0], [x1, y1], [x2, y2], [x3, y3]]) => {
  const sx = x0 - x1 + x2 - x3;
  const sy = y0 - y1 + y2 - y3;
  if (Math.abs(sx) < 1e-9 && Math.abs(sy) < 1e-9) return [x1 - x0, x3 - x0, x0, y1 - y0, y3 - y0, y0, 0, 0];
  const dx1 = x1 - x2;
  const dx2 = x3 - x2;
  const dy1 = y1 - y2;
  const dy2 = y3 - y2;
  const den = dx1 * dy2 - dx2 * dy1;
  const g = (sx * dy2 - dx2 * sy) / den;
  const h = (dx1 * sy - sx * dy1) / den;
  return [x1 - x0 + g * x1, x3 - x0 + h * x3, x0, y1 - y0 + g * y1, y3 - y0 + h * y3, y0, g, h];
};

/** CSS transform for a w x h layer (transform-origin 0 0) so its corners land on the quad of a w x h page. */
export const perspectiveTransform = (perspective, w, h) => {
  const p = normalizePerspective(perspective);
  if (!p || !w || !h) return null;
  const [a, b, c, d, e, f, g, hh] = squareToQuad(toPoints(p).map(([x, y]) => [(x / 100) * w, (y / 100) * h]));
  const m = [a / w, d / w, 0, g / w, b / h, e / h, 0, hh / h, 0, 0, 1, 0, c, f, 0, 1];
  return `matrix3d(${m.map((v) => +v.toFixed(10)).join(',')})`;
};

/** Style for the layer that holds the design (text, elements, selection) on a w x h page. */
export const perspectiveSurfaceStyle = (perspective, w, h) => {
  const transform = perspectiveTransform(perspective, w, h);
  if (!transform) return null;
  return { position: 'absolute', left: 0, top: 0, width: w, height: h, transformOrigin: '0 0', transform, overflow: 'hidden' };
};

/** A point on the page (% of the page) -> the same point on the flat design (% of the flat page). */
export const pageToFlat = (perspective, xPct, yPct) => {
  const p = normalizePerspective(perspective);
  if (!p) return [xPct, yPct];
  const [a, b, c, d, e, f, g, h] = squareToQuad(toPoints(p));
  // invert [[a b c] [d e f] [g h 1]] applied to (x, y, 1)
  const A = e - f * h;
  const B = f * g - d;
  const C = d * h - e * g;
  const D = c * h - b;
  const E = a - c * g;
  const F = b * g - a * h;
  const G = b * f - c * e;
  const H = c * d - a * f;
  const I = a * e - b * d;
  const w = C * xPct + F * yPct + I;
  if (Math.abs(w) < 1e-12) return [xPct, yPct];
  return [((A * xPct + D * yPct + G) / w) * 100, ((B * xPct + E * yPct + H) / w) * 100];
};

/** Default corners for a new mockup: a gentle tilt the admin then drags onto the card in the photo. */
export const DEFAULT_PERSPECTIVE = { tl: [18, 12], tr: [84, 18], br: [80, 88], bl: [14, 82] };

/**
 * The corners moved into a box of another aspect ratio that shows the letter-shaped background with
 * object-fit: cover (e.g. the 5:7 template cards), so they stay on the card in the cropped photo.
 */
export const coverPerspective = (perspective, boxAspect, pageAspect = 8.5 / 11) => {
  const p = normalizePerspective(perspective);
  if (!p || !boxAspect || Math.abs(boxAspect - pageAspect) < 0.001) return p;
  const kx = boxAspect < pageAspect ? pageAspect / boxAspect : 1; // photo wider than the box: sides cropped
  const ky = boxAspect > pageAspect ? boxAspect / pageAspect : 1; // photo taller: top and bottom cropped
  return checked(Object.fromEntries(CORNERS.map((k) => [k, [50 + (p[k][0] - 50) * kx, 50 + (p[k][1] - 50) * ky]])));
};

/** The corners on the background as shown: a mirrored photo (background.flip_h) mirrors the card too. */
export const perspectiveForBackground = (perspective, background) => {
  const p = normalizePerspective(perspective);
  if (!p || !background?.flip_h) return p;
  const m = (k) => [100 - p[k][0], p[k][1]];
  return checked({ tl: m('tr'), tr: m('tl'), br: m('bl'), bl: m('br') });
};
